import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import { Button } from '@/components/ui/button';
import { RecommendationPicker } from '@/components/rebuild/RecommendationPicker';
import { RebuildProgressBanner } from '@/components/rebuild/RebuildProgressBanner';
import { RebuildScriptViewer } from '@/components/rebuild/RebuildScriptViewer';
import { RebuildChangesPanel } from '@/components/rebuild/RebuildChangesPanel';
import { useRebuildSession } from '@/hooks/useRebuildSession';

const RebuildScript: React.FC = () => {
  const { analysisSessionId } = useParams<{ analysisSessionId: string }>();
  const navigate = useNavigate();
  const [jumpEditId, setJumpEditId] = useState<string | null>(null);

  const {
    session,
    available,
    currentText,
    setCurrentText,
    edits,
    loading,
    error,
    polling,
    runRebuild,
    toggleEdit,
    saveContent,
    finalizeExport,
  } = useRebuildSession(analysisSessionId);

  const showPicker =
    !loading &&
    session &&
    !polling &&
    (session.status === 'selecting' ||
      session.status === 'error' ||
      (session.status === 'complete' && edits.length === 0));
  const showReview =
    session &&
    session.status === 'complete' &&
    edits.length > 0 &&
    !!currentText &&
    !polling;
  const showProgress = polling || session?.status === 'in_progress';

  const handleExport = async () => {
    await finalizeExport();
    if (currentText && session?.rebuild_filename) {
      const blob = new Blob([currentText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = session.rebuild_filename;
      a.click();
      URL.revokeObjectURL(url);
    }
    navigate('/workflow');
  };

  return (
    <IndexWorkspaceShell activeNav="audit">
      <main className="mx-auto max-w-6xl">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="ni-label mb-2">Module · NucliusAudit</p>
            <Button
              variant="indexOutline"
              onClick={() => navigate('/workflow')}
              className="-ml-2 mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to audit
            </Button>
            <h1 className="text-2xl font-sans font-bold text-white">Script rebuild</h1>
            <p className="mt-2 text-sm text-white/45 font-sans">
              {session?.script_title && (
                <span className="text-white/70 font-mono">{session.script_title}</span>
              )}
              {session?.rebuild_filename && (
                <span className="text-white/45"> · {session.rebuild_filename}</span>
              )}
            </p>
            {session?.text_format_note && (
              <p className="mt-1 text-xs text-amber-200/60 font-sans">{session.text_format_note}</p>
            )}
          </div>
          {showReview && (
            <Button
              onClick={() => void handleExport()}
              className="shrink-0 bg-gold-button text-black hover:bg-gold-light font-sans font-semibold uppercase tracking-wider rounded-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Export & finish
            </Button>
          )}
        </header>

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300 text-sm font-sans">
            {error}
          </div>
        )}

        {loading && (
          <p className="text-white/50 font-sans text-center py-12">Loading rebuild session…</p>
        )}

        {showProgress && (
          <div className="mb-6">
            <RebuildProgressBanner progress={session?.progress} polling={polling} />
          </div>
        )}

        {showPicker && !showProgress && (
          <RecommendationPicker
            available={available}
            loading={polling}
            onStart={(sel) => void runRebuild(sel)}
          />
        )}

        {showReview && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <RebuildScriptViewer
                currentText={currentText}
                edits={edits}
                onChange={setCurrentText}
                onSave={(t) => void saveContent(t)}
                highlightEditId={jumpEditId}
              />
            </div>
            <div className="lg:col-span-1">
              <h2 className="text-sm font-sans font-semibold text-white/70 uppercase tracking-wider mb-3">
                Changes ({edits.filter((e) => e.status === 'applied').length} applied)
              </h2>
              <RebuildChangesPanel
                edits={edits}
                onToggle={(id, status) => void toggleEdit(id, status)}
                onJumpTo={(id) => setJumpEditId(id)}
              />
            </div>
          </div>
        )}

        {session?.status === 'selecting' && !available.length && !loading && (
          <p className="text-white/50 font-sans text-center py-12">
            No actionable recommendations found on this scorecard. Run a full analysis first —
            recommendations are drawn from Engagement, Commerciality, and the full agent output appendix.
          </p>
        )}
      </main>
    </IndexWorkspaceShell>
  );
};

export default RebuildScript;
